/**
 * FundraiserStore.ts - Main MobX Store for Fundraiser Application State
 *
 * This is the central state management store that handles all fundraiser data,
 * builder state, navigation, and business logic. It uses MobX for reactive state management.
 *
 * Key Responsibilities:
 * - Fundraiser CRUD operations and data management
 * - Builder state and validation management
 * - Navigation and view state management
 * - Donation and promoter data handling
 * - Layout and design persistence
 * - Gallery settings management
 *
 * Key Features:
 * - Observable state with computed properties
 * - Builder data initialization and persistence
 * - Layout zone management for drag-and-drop
 * - Validation and error handling
 * - Local storage integration for layout data
 * - Statistics and analytics calculations
 *
 * Dependencies: MobX, initial data, type definitions
 */

import {
  makeObservable,
  observable,
  action,
  computed,
  runInAction,
} from "mobx";
import { OpenAIService } from "../utils/openaiService";
import { FundraiserService } from "../services";
import {
  ColorSwatches,
  DonationFormType,
  DonationPopupModalType,
  FundRaiserRedirectType,
  FundRaiserStatus,
  FundRaiserTemplateLayoutType,
  IconType,
  LegalTermsAndConditionType,
  RedirectOtherFundraiserType,
} from "../enums";
import { IDonation } from "../interfaces";
import { firstValueFrom } from "rxjs";
import { AuthService, IColor } from "@mindmemobile/mm-shared-global";
import {
  CategoryModel,
  ColorModel,
  ContactReferenceDataModel,
  CreateFundraiserModel,
  DonationItemModel,
  DonationModel,
  FundraiserDonationButtonColors,
  FundRaiserGridModel,
  FundraisersDonationsBoardResponse,
  GallerySettingsModel,
  ListInterestTagModel,
  OpenAISettingsModel,
  PromoterProgressModel,
  SubscriptionModel,
} from "../models";
import { Fundraiser } from "../models/FundraisersGridModels";
import {
  ContactService,
  ContactReferenceModel,
  MerchantAccount,
  CountryStore,
  PaymentMethodModel,
  ApplicationStatus,
  SubscriptionStatus,
  ApplicationSettingsStore,
  ApplicationSubAccountBusinessModel,
  AddressModel,
} from "@mindmemobile/mm-payment-modules";
import { MobilePageModel } from "../models/MobilePageModels";
import { ImageQuality } from "@mindmemobile/mm-shared-library";

export class FundraiserStore {
  fundraisersGridData: FundRaiserGridModel[] = [];
  isLoadingFundRaisersGridData: boolean = true;
  fundraisers: CreateFundraiserModel[] = [];
  donations: FundraisersDonationsBoardResponse[] = [];
  promoters: PromoterProgressModel[] = [];
  subscriptions: SubscriptionModel[] = [];
  categories: CategoryModel[] = [];
  selectedFundraiserId: string | null = null;
  selectedFundraiserDetails: CreateFundraiserModel =
    new CreateFundraiserModel();
  selectedPromoterId: string | null = null;
  selectedTab: string = "details";
  showSuccessToast: boolean = false;
  currentBuilderStep: number = 1;
  showMediaError: boolean = false;
  shouldScrollToMediaSection: boolean = false;
  shouldScrollToImpactSection: boolean = false;
  shouldScrollToStatsSection: boolean = false;
  gallerySettings: GallerySettingsModel = new GallerySettingsModel();
  galleryFundRaisers: CreateFundraiserModel[] = [];
  countryStore = new CountryStore();
  subAccountBusinessInfo: ApplicationSubAccountBusinessModel | null = null;
  // Layout zones for drag and drop functionality
  layoutZones: Record<string, string[]> = {
    topZone: [
      "mission",
      "impact",
      "fundraiserProgress",
      "promoterProgress",
      "stats",
    ],
    leftZone: ["media", "donation"],
    rightZone: ["cta", "promoters", "recent"],
    bottomZone: ["description", "sponsor"],
  };

  builderData: CreateFundraiserModel = new CreateFundraiserModel();

  savedBuilderData: CreateFundraiserModel = new CreateFundraiserModel({
    ...this.builderData,
  });
  aiGeneratedData: any = null;
  donationItems: DonationItemModel[] = [];
  merchantAccounts: MerchantAccount[] = [];
  // Non-observable in-flight guard: key of the merchant-accounts fetch currently running,
  // used to dedupe concurrent identical calls. Intentionally not in makeObservable.
  private merchantFetchInFlightKey: string | null = null;
  // Non-observable: id of the merchant account whose default surcharge settings have
  // already been applied to the builder. Keeps the donation step from re-applying the
  // account defaults (and wiping the user's own choices) on every remount.
  surchargeDefaultsAppliedForMerchantId: string | null = null;
  lists: ListInterestTagModel[] = [];
  tags: ListInterestTagModel[] = [];
  interests: ListInterestTagModel[] = [];
  contactReferences: ContactReferenceDataModel =
    new ContactReferenceDataModel();
  contacts: ContactReferenceModel[] = [];
  mobilePages: MobilePageModel[] = [];
  lastExpandedSections: Set<string> = new Set(["details"]);
  fundraiserDetailsPageData: CreateFundraiserModel =
    new CreateFundraiserModel();
  public HIGH_QUALITY_IMAGE_CHARGES = 0.24836;
  public LOW_QUALITY_IMAGE_CHARGES = 0.312;
  public isAICreditsDataNull: boolean = false;
  //openAISettings: OpenAISettingsModel | null = null;

  constructor() {
    makeObservable(this, {
      countryStore: observable,
      fundraisers: observable,
      donations: observable,
      promoters: observable,
      lastExpandedSections: observable,
      subscriptions: observable,
      selectedFundraiserId: observable,
      selectedPromoterId: observable,
      selectedTab: observable,
      showSuccessToast: observable,
      currentBuilderStep: observable,
      showMediaError: observable,
      shouldScrollToMediaSection: observable,
      shouldScrollToImpactSection: observable,
      shouldScrollToStatsSection: observable,
      builderData: observable,
      savedBuilderData: observable,
      aiGeneratedData: observable,
      layoutZones: observable,
      categories: observable,
      gallerySettings: observable,
      selectedFundraiserDetails: observable,
      //openAISettings: observable,
      contacts: observable,
      fundraiserDetailsPageData: observable,
      // Actions
      setSelectedFundraiser: action,
      setSelectedPromoter: action,
      setSelectedTab: action,
      setShowSuccessToast: action,
      initializeBuilder: action,
      updateBuilderField: action,
      saveBuilder: action,
      resetBuilder: action,
      setShouldScrollToMediaSection: action,
      setShouldScrollToImpactSection: action,
      setShouldScrollToStatsSection: action,
      setSubAccountBusinessInfo: action,
      setBussinessAddressInBuilder: action,

      addDonation: action,
      addSubscription: action,
      updateDonation: action,
      hasActiveMerchantAccount: action,
      // updatePromoter: action,
      deleteFundraiser: action,
      setPromoters: action,
      setDonations: action,
      updateGallerySettings: action,
      loadGallerySettings: action,
      saveGallerySettings: action,
      setAIGeneratedData: action,
      clearAIGeneratedData: action,
      fetchDonationItems: action,
      fetchMerchantAccounts: action,
      fetchListInterestTags: action,
      fetchMindMeContactReferences: action,
      fetchMindMeContacts: action,
      fetchMobilePages: action,
      setOpenAISettings: action,
      getAccountBussinessInfo: action,

      // Computed values
      selectedFundraiser: computed,
      currentPromoter: computed,
      goal: computed,
      currentAmount: computed,
      remainingAmount: computed,
      getFilteredGalleryFundraisers: computed,
      topPerformingFundraisers: computed,
      totalRaisedAllTime: computed,
      totalRaisedThisMonth: computed,
      totalFundraisers: computed,
      totalDonors: computed,
      fundraisersGridData: observable,
      isLoadingFundRaisersGridData: observable,
    });
  }

  setOpenAISettings(settings: OpenAISettingsModel) {
    //   this.openAISettings = settings;
  }

  setIsAICreditsDataNull(isNull: boolean) {
    this.isAICreditsDataNull = isNull;
  }

  /* The goal a newly added promoter starts with: the fundraiser's own "Promoter Goal" (Marketing).
     This used to be a fixed 500 that nothing ever set, so the setting was ignored. 0 = no goal. */
  getDefaultPromoterGoal(fundraiserId?: string): number {
    const fundraiser = this.getPromoterGoalSource(fundraiserId);
    return fundraiser?.promoterGoal && fundraiser.promoterGoal > 0
      ? fundraiser.promoterGoal
      : 0;
  }

  // The fundraiser being worked on: the builder's when it matches, else the details page's.
  getPromoterGoalSource(fundraiserId?: string): CreateFundraiserModel | null {
    if (fundraiserId && this.fundraiserDetailsPageData?.id === fundraiserId) {
      return this.fundraiserDetailsPageData;
    }
    if (!fundraiserId || this.builderData?.id === fundraiserId || fundraiserId === "1") {
      return this.builderData;
    }
    return this.fundraiserDetailsPageData ?? null;
  }

  setFundraiserDetailPageData(data?: CreateFundraiserModel) {
    this.fundraiserDetailsPageData = new CreateFundraiserModel(data);
  }

  setCredits(highQualityCredits: number, lowQualityCredits: number) {
    this.HIGH_QUALITY_IMAGE_CHARGES = highQualityCredits;
    this.LOW_QUALITY_IMAGE_CHARGES = lowQualityCredits;
  }

  getCalculatedTotalCredits(imageQuality: ImageQuality | null): number {
    const quality = imageQuality?.toLowerCase?.() ?? "";
    const isHigh = quality === "high" || quality === "auto" || !quality;
    const imageCharge = isHigh
      ? this.HIGH_QUALITY_IMAGE_CHARGES
      : this.LOW_QUALITY_IMAGE_CHARGES;
    return imageCharge;
  }

  setCategories(data: CategoryModel[]) {
    this.categories = data;
  }
  // Computed Properties
  get selectedFundraiser() {
    return this.selectedFundraiserDetails;
  }

  setSelectedFundraiser(id: string | null) {
    this.selectedFundraiserId = id;
  }

  setFundraiserDetails(fundraiser: CreateFundraiserModel) {
    this.selectedFundraiserDetails = fundraiser;
  }

  get currentPromoter() {
    return this.promoters.find((p) => p.id === this.selectedPromoterId) || null;
  }

  get goal(): number {
    return this.selectedFundraiserDetails?.goal || 0;
  }

  get currentAmount(): number {
    return this.selectedFundraiserDetails?.totalRaisedAmount || 0;
  }

  get remainingAmount(): number {
    return Math.max(0, this.goal - this.currentAmount);
  }

  get activeFundraisers() {
    return this.fundraisers.filter((f) => f.status === FundRaiserStatus.Active);
  }

  get topPerformingFundraisers() {
    return this.fundraisers
      .filter(
        (f) =>
          f.status === FundRaiserStatus.Active ||
          f.status === FundRaiserStatus.Completed
      )
      .filter((f) => f.totalRaisedAmount > 0)
      .sort((a, b) => b.totalRaisedAmount - a.totalRaisedAmount)
      .slice(0, 3);
  }

  get totalRaisedAllTime() {
    return this.fundraisers.reduce(
      (total, f) => total + f.totalRaisedAmount,
      0
    );
  }

  get totalRaisedThisMonth() {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return this.donations
      .filter((d) => d.timestamp >= thisMonth)
      .reduce((total, d) => total + d.amount, 0);
  }

  get totalFundraisers() {
    return this.fundraisers.filter((f) => f.status === FundRaiserStatus.Active)
      .length;
  }

  get totalDonors() {
    const uniqueDonors = new Set();
    this.donations.forEach((d) => {
      if (d.donorEmail) {
        uniqueDonors.add(d.donorEmail);
      } else {
        uniqueDonors.add(d.donorName);
      }
    });
    return uniqueDonors.size;
  }

  setSelectedPromoter = (promoterId: string | null) => {
    this.selectedPromoterId = promoterId;
  };

  setSelectedTab = (tab: string) => {
    this.selectedTab = tab;
  };

  setShowSuccessToast = (show: boolean) => {
    this.showSuccessToast = show;
  };

  // AI Data Management Methods
  setAIGeneratedData = (data: any) => {
    this.aiGeneratedData = data;
  };

  clearAIGeneratedData = () => {
    this.aiGeneratedData = null;
  };

  // Data Management Methods
  addDonation = (
    fundraiserId: string,
    amount: number,
    firstName: string,
    lastName: string,
    email: string,
    comment: string,
    isAnonymous: boolean,
    phone?: string,
    paymentMethod: "card" | "bank" = "card",
    isMonthly: boolean = false,
    promoterId?: string
  ) => {
    const newDonation: FundraisersDonationsBoardResponse =
      new FundraisersDonationsBoardResponse({
        id: `don-${Date.now()}`,
        amount,
        donorName: isAnonymous ? "Anonymous Donor" : `${firstName} ${lastName}`,
        firstName,
        lastName,
        isAnonymous,
        donorEmail: email,
        donorMobile: phone
          ? {
              phoneNumber: phone,
              countryId:
                this.countryStore.countriesWithState.find((x) =>
                  x.getUSCountry()
                )?.id || "",
              countryCode:
                this.countryStore.countriesWithState
                  .find((x) => x.getUSCountry())
                  ?.code?.toString() || "",
            }
          : undefined,
        comment,
        timestamp: new Date(),
        fundraiserId: fundraiserId,
        promoterId,
        isMonthly,
        paymentMethod: new PaymentMethodModel(),
      });

    this.donations.push(newDonation);

    // If this is a monthly donation, also create a subscription
    if (isMonthly) {
      this.addSubscription(
        fundraiserId,
        amount,
        firstName,
        lastName,
        email,
        isAnonymous,
        phone,
        paymentMethod,
        promoterId
      );
    }

    // Update fundraiser current amount
    const fundraiser = this.fundraisers.find((f) => f.id === fundraiserId);
    if (fundraiser) {
      fundraiser.totalRaisedAmount += amount;
      fundraiser.totalDonations += 1;
      fundraiser.lastDonatedOn = new Date();
    }
  };

  addSubscription = (
    fundraiserId: string,
    monthlyAmount: number,
    firstName: string,
    lastName: string,
    email: string,
    isAnonymous: boolean,
    phone?: string,
    paymentMethod: "card" | "bank" = "card",
    promoterId?: string
  ) => {
    // Calculate next due date (30 days from now)
    const nextDueDate = new Date();
    nextDueDate.setDate(nextDueDate.getDate() + 30);

    const newSubscription = new SubscriptionModel({
      id: `sub-${Date.now()}`,
      donorName: isAnonymous ? "Anonymous Donor" : `${firstName} ${lastName}`,
      firstName,
      lastName,
      isAnonymous,
      donorEmail: email,
      donorMobile: phone,
      monthlyAmount,
      nextDueDate: nextDueDate,
      totalPaid: monthlyAmount, // First payment already made
      paymentCount: 1,
      status: SubscriptionStatus.Active,
      startDate: new Date(),
      fundraiserId,
      promoterId,
      // paymentMethod: {
      //   type: paymentMethod,
      //   brand: (paymentMethod === "card" ? "visa" : undefined) as
      //     | "visa"
      //     | "mastercard"
      //     | "amex"
      //     | "discover",
      //   last4: paymentMethod === "card" ? "1234" : undefined,
      //   last6: paymentMethod === "bank" ? "123456" : undefined,
      // },
    });

    // Add to subscriptions array
    this.subscriptions.push(newSubscription);

    return newSubscription;
  };

  updatePromoter = (
    promoterId: string,
    updatedPromoter: PromoterProgressModel
  ) => {
    const index = this.promoters.findIndex((p) => p.id === promoterId);
    if (index !== -1) {
      this.promoters[index] = updatedPromoter;
    }
  };

  updateDonation = (
    donationId: string,
    updates: Partial<FundraisersDonationsBoardResponse>
  ) => {
    const index = this.donations.findIndex((d) => d.id === donationId);
    if (index === -1) return;
    const existing = this.donations[index];
    const updated = new FundraisersDonationsBoardResponse({
      ...existing,
      ...updates,
    });

    this.donations[index] = updated;
  };

  setPromoters(data: PromoterProgressModel[]) {
    this.promoters = data;
  }

  setDonations(data: FundraisersDonationsBoardResponse[]) {
    this.donations = data;
  }

  setSubscriptions(data: SubscriptionModel[]) {
    this.subscriptions = data;
  }

  deleteFundraiser = (fundraiserId: string) => {
    this.fundraisers = this.fundraisers.filter((f) => f.id !== fundraiserId);

    // Also remove related data
    this.donations = this.donations.filter(
      (d) => d.fundraiserId !== fundraiserId
    );
    this.promoters = this.promoters.filter(
      (p) => p.fundraiserId !== fundraiserId
    );

    // Navigate back to dashboard
    this.selectedFundraiserId = null;
  };

  getPromoterById = (promoterId: string) => {
    console.log(this.promoters);
    return this.promoters.find((p) => p.id === promoterId) || null;
  };

  getFundraiserPromoters = (fundraiserId: string) => {
    return this.promoters.filter((p) => p.fundraiserId === fundraiserId);
  };

  getFundraiserDonations = (fundraiserId: string) => {
    return this.donations
      .filter((d) => d.fundraiserId === fundraiserId)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  };

  getPromoterDonations = (promoterId: string, fundraiserId: string) => {
    return this.donations
      .filter(
        (d) => d.fundraiserId === fundraiserId && d.promoterId === promoterId
      )
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  };

  getFundraisersByContactId = (contactId: string) => {
    // Get all fundraisers where the contact is a promoter
    const promoterFundraiserIds = this.promoters
      .filter((p) => p.id === contactId)
      .map((p) => p.fundraiserId);

    return this.fundraisers.filter((f) => promoterFundraiserIds.includes(f.id));
  };

  getFundraiserSubscriptions = (fundraiserId: string) => {
    return this.subscriptions
      .filter((s) => s.fundraiserId === fundraiserId)
      .sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  };

  fetchDonationItems = () => {
    FundraiserService.getAllDonationItems(
      AuthService.authData.accountId || ""
    ).subscribe((response) => {
      if (response.result) {
        const data = response.result.map((d) =>
          DonationItemModel.deserializeAPIModel2(d)
        );
        this.donationItems = data;
      }
    });
  };

  fetchMerchantAccounts = (searchValue: string) => {
    if (!AuthService.authData.accountId) {
      this.merchantAccounts = [];
      return;
    }
    // Dedupe concurrent calls for the same account+search - the dashboard effect and the
    // builder both trigger this, and a single mount can fire it more than once, which was
    // producing multiple identical hits to the merchant-accounts API.
    const key = `${AuthService.authData.accountId}|${searchValue}`;
    if (this.merchantFetchInFlightKey === key) {
      return;
    }
    this.merchantFetchInFlightKey = key;
    FundraiserService.getMerchantAccounts(
      AuthService.authData.accountId || "",
      1,
      999,
      searchValue
    ).subscribe({
      next: (response) => {
        if (response.result) {
          const data = response.result.map((d) =>
            MerchantAccount.deserializeAPIModel(d)
          );
          this.merchantAccounts = data;
        }
      },
      error: () => {
        this.merchantFetchInFlightKey = null;
      },
      complete: () => {
        this.merchantFetchInFlightKey = null;
      },
    });
  };

  fetchListInterestTags = () => {
    if (!AuthService.authData.accountId) {
      this.lists = [];
      this.interests = [];
      this.tags = [];
      return;
    }
    FundraiserService.getMindMeListInterestTag(
      AuthService.authData.accountId || ""
    ).subscribe((response) => {
      const data = response.result;
      if (data) {
        this.lists =
          data?.lists?.map((l: any) => ListInterestTagModel.deserialize(l)) ||
          [];
        this.interests =
          data?.interests?.map((l: any) =>
            ListInterestTagModel.deserialize(l)
          ) || [];
        this.tags =
          data?.tags?.map((l: any) => ListInterestTagModel.deserialize(l)) ||
          [];
      }
    });
  };

  fetchMindMeContactReferences = () => {
    FundraiserService.getMindMeContactReferences(
      AuthService.authData.accountId || ""
    ).subscribe((response) => {
      this.contactReferences = ContactReferenceDataModel.deserialize(
        response.result
      );
    });
  };

  fetchMindMeContacts = () => {
    ContactService.getMindMeContacts(
      AuthService.authData.accountId || "",
      "",
      "",
      "",
      9999999
    ).subscribe((response) => {
      const contacts =
        response?.result?.data?.map((contact) =>
          ContactReferenceModel.deserializeFromMindMeContacts(contact)
        ) || [];

      this.contacts = contacts.filter((c) => c.displayValue.trim() !== "");
    });
  };

  fetchMobilePages = () => {
    FundraiserService.getMindMeMobilePages(
      AuthService.authData.accountId || ""
    ).subscribe((response) => {
      this.mobilePages =
        response?.result?.map((mobilePage) =>
          MobilePageModel.deserializeAPIModel(mobilePage)
        ) || [];
    });
  };

  getAccountBussinessInfo = () => {
    FundraiserService.getAccountBusinessInfo(
      AuthService.authData.accountId || ""
    ).subscribe((response) => {
      this.subAccountBusinessInfo =
        ApplicationSubAccountBusinessModel.deserializeAPIModel(response.result);
    });
  };

  hasActiveMerchantAccount = () => {
    return this.merchantAccounts.some(
      (account) => account.applicationStatus === ApplicationStatus.Active
    );
  };

  getDonationAmountsForFundraiser = (fundraiserId: string) => {
    return this.getFundraiserDonations(fundraiserId).map((d) => d.amount);
  };

  getPaymentMethodBreakdownForFundraiser = (fundraiserId: string) => {
    const donations = this.getFundraiserDonations(fundraiserId);
    const methodCounts = new Map<string, number>();

    donations.forEach((donation) => {
      const method =
        donation.paymentMethod?.type === "Card"
          ? donation.paymentMethod?.type && donation.paymentMethod?.card?.brand
            ? donation.paymentMethod?.card?.brand?.charAt(0)?.toUpperCase() +
              donation.paymentMethod?.card?.brand?.slice(1)
            : "Card"
          : "ACH";

      methodCounts.set(method, (methodCounts.get(method) || 0) + 1);
    });

    const total = donations.length;
    return Array.from(methodCounts.entries()).map(([method, count]) => ({
      method,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
    }));
  };

  getDonationButtonStyles = () => {
    const colors =
      this.builderData.donationButtonColors ||
      new FundraiserDonationButtonColors();

    const type = this.builderData.donationButtonType || "gradient";

    const background =
      type === "solid"
        ? colors.primary.rgba || colors.primary.hex
        : `linear-gradient(135deg, ${colors.primary.rgba || colors.primary.hex}, ${colors.secondary.rgba || colors.secondary.hex})`;

    return {
      background,
      color: colors.text.rgba || colors.text.hex || "#ffffff",
      boxShadow: `0 4px 16px ${colors.primary.rgba || colors.primary.hex}30`,
      "--hover-shadow": `0 8px 24px ${colors.primary.rgba || colors.primary.hex}40`,
    } as React.CSSProperties & { "--hover-shadow": string };
  };

  initializeBuilder = (fundraiser?: CreateFundraiserModel) => {
    if (!fundraiser) {
      this.setBussinessAddressInBuilder(AuthService.shortUniqueId);
      this.savedBuilderData = new CreateFundraiserModel({
        ...this.builderData,
      });
      this.fundraiserDetailsPageData = new CreateFundraiserModel({
        ...this.builderData,
      });
      return;
    }
    const hasData = Object.values(fundraiser?.layoutZones).some(
      (zoneArray) => zoneArray.length > 0
    );
    if (hasData) {
      this.layoutZones = fundraiser.layoutZones;
    }

    this.savedBuilderData = new CreateFundraiserModel({ ...this.builderData });
  };

  setBussinessAddressInBuilder = (shortUniqueId: string) => {
    const businessCountry = this.countryStore.countriesWithState.find(
      (c) => c.id === this.subAccountBusinessInfo?.countryId
    );
    const state = businessCountry?.states?.find(
      (s) => s.id === this.subAccountBusinessInfo?.stateId
    );

    //set legal section default data
    this.builderData = new CreateFundraiserModel({
      shortUniqueId,
      countryId: businessCountry?.id || "",
      country: businessCountry,
      stateId: state?.id || "",
      state: state?.id || "",
      zip: this.subAccountBusinessInfo?.zipCode || "",
      city: this.subAccountBusinessInfo?.city || "",
      streetAddress1: this.subAccountBusinessInfo?.businessAddress1 || "",
      streetAddress2: this.subAccountBusinessInfo?.businessAddress2 || "",
      legalName: this.subAccountBusinessInfo?.businessName || "",
    });
  };

  updateBuilderField = (field: keyof CreateFundraiserModel, value: any) => {
    this.builderData = new CreateFundraiserModel({
      ...this.builderData,
      [field]: value,
    });

    this.selectedFundraiserDetails = new CreateFundraiserModel({
      ...this.selectedFundraiserDetails,
      [field]: value,
    });
  };

  resetBuilder = (shortUniqueId: string) => {
    this.setBussinessAddressInBuilder(shortUniqueId);
  };

  setBuilderData = (data: Partial<CreateFundraiserModel>) => {
    this.builderData = new CreateFundraiserModel({
      ...this.builderData,
      ...data,
    });
    this.selectedFundraiserDetails = new CreateFundraiserModel({
      ...this.selectedFundraiserDetails,
      ...data,
    });
  };

  saveBuilder = async (): Promise<string | null> => {
    try {
      const saveRequest = CreateFundraiserModel.createAPIRequest(
        AuthService.mindmeAccountDetails?.userId || "",
        AuthService.authData.accountId || "",
        this.builderData,
        this.layoutZones
      );

      // UPDATE
      if (this.builderData.id) {
        const updateRequest = {
          ...saveRequest,
          id: this.builderData.id,
        };

        const response = await firstValueFrom(
          FundraiserService.updateFundraiser(updateRequest)
        );

        return response?.result ? this.builderData.id : null;
      }

      // CREATE
      const response = await firstValueFrom(
        FundraiserService.createFundraiser(saveRequest)
      );

      if (response?.result) {
        this.selectedFundraiserId = response.result;
        return response.result;
      }

      return null;
    } catch (error) {
      console.error("Error saving fundraiser:", error);
      return null;
    }
  };

  setShouldScrollToMediaSection = (should: boolean) => {
    this.shouldScrollToMediaSection = should;
  };

  setShouldScrollToImpactSection = (should: boolean) => {
    this.shouldScrollToImpactSection = should;
  };

  setShouldScrollToStatsSection = (should: boolean) => {
    this.shouldScrollToStatsSection = should;
  };

  setSubAccountBusinessInfo = (info: ApplicationSubAccountBusinessModel) => {
    this.subAccountBusinessInfo = info;
  };

  loadFundraiserLayoutData = (fundraiserId: string) => {
    try {
      const key = `fundraiser_layout_${fundraiserId}`;
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error loading fundraiser layout data:", error);
      return null;
    }
  };

  findSectionZone = (sectionId: string) => {
    for (const [zoneId, sections] of Object.entries(this.layoutZones)) {
      if (sections.includes(sectionId)) {
        return {
          zoneId: zoneId as keyof FundraiserStore["layoutZones"],
          index: sections.indexOf(sectionId),
        };
      }
    }
    return null;
  };

  moveSectionToZone = (
    sectionId: string,
    targetZone: keyof FundraiserStore["layoutZones"],
    targetIndex?: number,
    preserveOrder = false
  ): boolean => {
    // Remove from current zone
    let removed = false;
    for (const [zoneKey, sections] of Object.entries(this.layoutZones)) {
      const index = sections.indexOf(sectionId);
      if (index !== -1) {
        sections.splice(index, 1);
        removed = true;
        break;
      }
    }

    if (!removed) return false;

    // Add to target zone
    const targetSections = this.layoutZones[targetZone];
    if (targetIndex !== undefined) {
      targetSections.splice(targetIndex, 0, sectionId);
    } else {
      targetSections.push(sectionId);
    }

    return true;
  };

  reorderSectionsInZone = (
    zoneId: keyof FundraiserStore["layoutZones"],
    activeId: string,
    overId: string
  ): boolean => {
    const zone = this.layoutZones[zoneId];
    const activeIndex = zone.indexOf(activeId);
    const overIndex = zone.indexOf(overId);

    if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
      return false;
    }

    // Remove and reinsert
    const [removed] = zone.splice(activeIndex, 1);
    zone.splice(overIndex, 0, removed);

    return true;
  };

  setLayoutZones = (zones?: Record<string, string[]>) => {
    this.layoutZones = zones ? zones : this.layoutZones;
  };

  // Gallery Methods
  updateGallerySettings = (
    field: keyof GallerySettingsModel,
    value: string | ColorModel | string[] | IColor | boolean | null
  ) => {
    this.gallerySettings = new GallerySettingsModel({
      ...this.gallerySettings,
      [field]: value,
    });
  };

  loadGallerySettings = async () => {
    try {
      const response = await firstValueFrom(
        FundraiserService.getGallerySettings(this.gallerySettings?.id || "")
      );
      if (response.result) {
        this.gallerySettings = GallerySettingsModel.deserializeAPIModel(
          response.result
        );
      }
    } catch (error) {
      console.error("Error loading gallery settings:", error);
    }
  };

  setGallerySettings = (settings: GallerySettingsModel) => {
    this.gallerySettings = settings;
  };

  saveGallerySettings = () => {
    try {
      localStorage.setItem(
        "gallery_settings",
        JSON.stringify(this.gallerySettings)
      );
      return true;
    } catch (error) {
      console.error("Error saving gallery settings:", error);
      return false;
    }
  };

  get getFilteredGalleryFundraisers() {
    let filtered = this.fundraisers;

    // Filter based on displayFundraisers setting
    switch (this.gallerySettings.displayFundraisers) {
      case "AllActive":
        filtered = filtered.filter((f) => f.status === FundRaiserStatus.Active);
        break;

      case "AllActiveStarted":
        // Show active fundraisers that have already started (start date <= today)
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today
        filtered = filtered.filter((f) => {
          if (f.status !== FundRaiserStatus.Active || !f.startDate)
            return false;
          const startDate =
            f.startDate instanceof Date ? f.startDate : new Date(f.startDate);
          return startDate <= today;
        });
        break;

      case "AllActiveStarting":
        // Show active fundraisers starting within X days (start date <= today + X days)
        const futureDate = new Date();
        futureDate.setDate(
          futureDate.getDate() +
            (parseFloat(this.gallerySettings.timeframeDays) || 3)
        );
        futureDate.setHours(23, 59, 59, 999); // End of future date
        filtered = filtered.filter((f) => {
          if (f.status !== FundRaiserStatus.Active || !f.startDate)
            return false;
          const startDate =
            f.startDate instanceof Date ? f.startDate : new Date(f.startDate);
          return startDate <= futureDate;
        });
        break;

      case "RecentlyAdded":
        // Show fundraisers created within the past X days
        const recentDaysAgo = new Date();
        recentDaysAgo.setDate(
          recentDaysAgo.getDate() -
            (parseFloat(this.gallerySettings.timeframeDays) || 90)
        );
        filtered = filtered.filter((f) => {
          if (!f.createdDate) return false;
          const createdDate =
            f.createdDate instanceof Date
              ? f.createdDate
              : new Date(f.createdDate);
          return createdDate >= recentDaysAgo;
        });
        break;

      case "Specific":
        // Show only specifically selected fundraisers
        if (
          this.gallerySettings.specificFundraiserIds &&
          this.gallerySettings.specificFundraiserIds.length > 0
        ) {
          filtered = filtered.filter((f) =>
            this.gallerySettings.specificFundraiserIds.includes(f.id)
          );
        } else {
          filtered = []; // No specific fundraisers selected
        }
        break;

      default:
        // Default to all active fundraisers
        filtered = filtered.filter((f) => f.status === FundRaiserStatus.Active);
        break;
    }
    return filtered;
  }

  // Add new fundraiser from AI-generated data
  addFundraiserFromAI = (aiData: CreateFundraiserModel) => {
    const newFundraiser: CreateFundraiserModel = new CreateFundraiserModel({
      id: aiData.id || `fundraiser-${Date.now()}`,
      name: aiData.name || "New Fundraiser",
      title: aiData.title || "Help Make a Difference",
      subtitle: aiData.subtitle || "",
      description: aiData.description || "",
      goal: aiData.goal || 10000,
      totalRaisedAmount: 0,
      totalDonations: 0,
      status: FundRaiserStatus.Draft,
      createdDate: new Date(),
      startDate: aiData.startDate
        ? new Date(aiData.startDate + "T00:00:00")
        : new Date(),
      endDate: aiData.endDate ? new Date(aiData.endDate + "T00:00:00") : null,
      lastDonatedOn: undefined,
      category: aiData.category || null,
      iconImageChoice: aiData.iconImageChoice || IconType.None,
      titleIcon: aiData.titleIcon || "Heart",
      titleColor: aiData.titleColor || new ColorModel({ hex: "#10b981" }),
      fundraiserImage: aiData.fundraiserImage || null,
      mediaItems: aiData.mediaItems || [],
      impact: aiData.impact || [],
      rewards: aiData.rewards || aiData.rewards || [],
      ranks: aiData.ranks || aiData.ranks || [],
      redirectOnVisit: FundRaiserRedirectType.None,
      redirectWebsiteUrl: "",
      redirectMobilePage: "page1",
      redirectOtherFundraiserType: RedirectOtherFundraiserType.ClosestGoal,
      closeOnGoalAchieved: false,
      increaseGoalOnAchieved: true,
    });

    // Set as selected and navigate to builder for final editing
    this.selectedFundraiserId = newFundraiser.id;

    // Initialize builder with the new fundraiser data
    this.initializeBuilder(newFundraiser);

    return newFundraiser;
  };

  // Create blank fundraiser for manual creation
  createBlankFundraiser = () => {
    this.selectedFundraiserId = null;
    this.initializeBuilder();
    return new CreateFundraiserModel();
  };

  setFundraisers(data: CreateFundraiserModel[]) {
    this.fundraisers = data;
  }

  setFundraisersGridData(data: FundRaiserGridModel[]) {
    this.fundraisersGridData = data;
  }

  toggleFundRaiserGridLoading(isLoading: boolean) {
    this.isLoadingFundRaisersGridData = isLoading;
  }

  setGalleryFundRaisers(data: CreateFundraiserModel[]) {
    this.galleryFundRaisers = data;
  }

  getGalleryLink(galleryId: string, promoterId: string) {
    return `${this.getFundRaiserDomainBaseURL()}/fundraiser/gallery/${galleryId}?PID=${promoterId || ""}`;
  }

  getPublicGalleryLinkByName(shortUniqueId, name: string, promoterId?: string) {
    return `${this.getFundRaiserDomainBaseURL()}/fundraiser/gallery/${shortUniqueId}/${encodeURIComponent(
      name
    )}?PID=${promoterId || ""}`;
  }

  getFundraiserPublicLink(fundraiserId: string, promoterId?: string) {
    return `${this.getFundRaiserDomainBaseURL()}/fundraiser/${fundraiserId}?PID=${promoterId || ""}`;
  }

  getFundraiserPublicLinkByShortUniqueId(
    shortUniqueId: string,
    pageName: string,
    promoterId: string
  ) {
    return `${this.getFundRaiserDomainBaseURL()}/fundraiser/${shortUniqueId}/${encodeURIComponent(
      pageName
    )}?PID=${promoterId || ""}`;
  }

  getFundRaiserDomainBaseURL() {
    return `https://${ApplicationSettingsStore.getFundraiserDomain()}`;
  }

  isDonationAllowed(isPending: boolean, status: keyof typeof FundRaiserStatus) {
    // Check if fundraiser is not active or is pending
    const isNotActive = status !== FundRaiserStatus.Active;

    if (isNotActive || isPending) {
      return; // Don't allow donation if not active or pending
    }
    return true;
  }

  getFundraiserPublicLinkFromModel(
    fundraiser: CreateFundraiserModel,
    promoterId: string
  ) {
    if (fundraiser.pageName)
      return `${this.getFundRaiserDomainBaseURL()}/fundraiser/${fundraiser.shortUniqueId}/${encodeURIComponent(
        fundraiser.pageName
      )}?PID=${promoterId || ""}`;
    return this.getFundraiserPublicLink(fundraiser.id || "", promoterId);
  }
}

export const fundraiserStore = new FundraiserStore();

// // Global function for testing GPT-5 models (accessible from browser console)
// (window as any).testGPT5Models = async () => {
//   const result = await OpenAIService.listGPT5Models();
//   return result;
// };
